import datapoints from './datapoints';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { MountScaleControl, UnmountScaleControl } from './ScaleControl';
import { WIDE_CONTENT_HOST_CLASS_NAME } from './WideContentHost';
import { logUsage } from 'owa-analytics';

const styles = require('./ScaleElementStyles.scss');
const MIN_SCALE_ATTR_NAME = 'min-scale';
const CONTAINER_MIN_HEIGHT = 40;
const HAS_HOVERED_ATTR_NAME = 'has-hovered';
const MAX_SCALE = 1;
const MOUSE_OVER_EVENT_NAME = 'mouseover';

export default function scaleElement(element: HTMLElement, availableWidth: number) {
    let currentWidth = element.offsetWidth;
    if (availableWidth > 0 && currentWidth > 0) {
        // offsetWidth excludes margins, need to extract them from availableWidth.
        availableWidth -= getLeftRightMargins(element);
        let scale = availableWidth / currentWidth;
        let elementWrapper: HTMLElement;
        if (scale < MAX_SCALE) {
            element.style.transform = 'scale(' + scale + ', ' + scale + ')';
            element.style.transformOrigin = getRTL() ? 'top right' : 'top left';
            element.setAttribute(MIN_SCALE_ATTR_NAME, scale.toString());

            // scale doesn't update element's height accordingly.
            // we should do so by creating a wrapper with updated height.
            // ensure container has min height of CONTAINER_MIN_HEIGHT so ScaleControl is shown in full.
            elementWrapper = wrap(element);
            elementWrapper.style.height =
                Math.max(getWrapperHeightWhenScaled(element, scale), CONTAINER_MIN_HEIGHT) + 'px';
        } else if (element.scrollWidth > availableWidth) {
            // This is the case where element has spilling content because it has descendants with fixed widths.
            elementWrapper = wrap(element);
        }

        if (elementWrapper) {
            logUsage(
                datapoints.WCScaledOrTruncated.name,
                datapoints.WCScaledOrTruncated.customData(scale)
            );
            elementWrapper.className = styles.container;
            elementWrapper.addEventListener(MOUSE_OVER_EVENT_NAME, onContainerHover);
            createScaleControl(elementWrapper, element);
        }
    }
}

export function undoScaleAllElements(elements: HTMLElement[]) {
    for (let i = elements.length - 1; i >= 0; i--) {
        // Not all table elements are scaled,
        // filter out the ones that don't have the scale containers.
        let container = elements[i].parentElement;
        if (container && container.className.indexOf(styles.container) < 0) {
            elements.splice(i, 1);
        }
    }

    if (elements.length > 0) {
        let wideContentHost = getWideContentHost(elements[0]);
        if (wideContentHost) {
            let shouldChangeHostWidth = true;
            let containers = wideContentHost.querySelectorAll(`.${styles.container}`);
            for (let i = 0; i < containers.length; i++) {
                let container = containers[i] as HTMLElement;
                let element = container.children[0] as HTMLElement;
                if (elements.indexOf(element) >= 0) {
                    undoScaleElement(element);
                } else {
                    // If there're still scaled elements not being undone in the host
                    // don't change host width.
                    // E.g. scaled elements are in first body and quoted body, which belong to the same host.
                    // When quoted body gets collapsed but first body is still shown, do not change the host width.
                    shouldChangeHostWidth = false;
                }
            }

            if (shouldChangeHostWidth) {
                shrinkWideContentHost(wideContentHost);
            }
        } else {
            elements.forEach(element => {
                undoScaleElement(element);
            });
        }
    }
}

function undoScaleElement(element: HTMLElement) {
    if (element.getAttribute(MIN_SCALE_ATTR_NAME)) {
        // found a scaled element, undo all operations.
        element.style.transform = '';
        element.style.transformOrigin = '';
        element.removeAttribute(MIN_SCALE_ATTR_NAME);
    }

    let elementWrapper = element.parentElement;
    if (elementWrapper.className.indexOf(styles.container) >= 0) {
        elementWrapper.removeEventListener(MOUSE_OVER_EVENT_NAME, onContainerHover);
        if (elementWrapper.getAttribute(HAS_HOVERED_ATTR_NAME)) {
            logUsage(datapoints.WCScaleContainerHovered.name);
            elementWrapper.removeAttribute(HAS_HOVERED_ATTR_NAME);
        }
        disposeScaleControl(elementWrapper);
        unwrap(element);
    }
}

function onContainerHover(e: MouseEvent) {
    let container = e.currentTarget as HTMLElement;
    container.removeEventListener(MOUSE_OVER_EVENT_NAME, onContainerHover);
    container.setAttribute(HAS_HOVERED_ATTR_NAME, 'true');
}

function getWrapperHeightWhenScaled(element: HTMLElement, scale: number): number {
    let currentHeight = element.offsetHeight;
    return Math.ceil(currentHeight * scale) + getTopBottomMargins(element);
}

function createScaleControl(container: HTMLElement, element: HTMLElement) {
    let scaleControlContainer = document.createElement('div');
    scaleControlContainer.className = styles.scaleControlContainer;
    // Prepend scaleControlContainer to container
    // Cannot use .prepend because it is not supported by IE.
    const containerContent = Array.prototype.slice.call(container.childNodes);
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    container.appendChild(scaleControlContainer);
    containerContent.forEach(childNode => container.appendChild(childNode));
    MountScaleControl(scaleControlContainer, onScaleUp(container, element));
}

function disposeScaleControl(container: HTMLElement) {
    let scaleControlContainers = container.querySelectorAll(`.${styles.scaleControlContainer}`);
    if (scaleControlContainers.length > 0) {
        let scaleControlContainer = scaleControlContainers[0] as HTMLElement;
        UnmountScaleControl(scaleControlContainer);
        container.removeChild(scaleControlContainer);
    }
}

let onScaleUp = (container: HTMLElement, element: HTMLElement) => () => {
    let wideContentHost = getWideContentHost(element);
    if (wideContentHost) {
        // Found the host, so we scale up all scaled elements.
        let maxWidthDelta = 0;
        let containers = wideContentHost.querySelectorAll(`.${styles.container}`);
        for (let i = 0; i < containers.length; i++) {
            let currentContainer = containers[i] as HTMLElement;
            disposeScaleControl(currentContainer);
            let currentElement = currentContainer.children[0] as HTMLElement;
            let currentWidthDelta = scaleUpElement(
                currentContainer,
                currentElement,
                currentElement == element /* needLogDatapoint */
            );
            maxWidthDelta = Math.max(maxWidthDelta, currentWidthDelta);
        }

        enlargeWideContentHost(wideContentHost, element, maxWidthDelta);
    } else {
        // If host is not found, just scale the current element.
        scaleUpElement(container, element, true /* needLogDatapoint */);
        // Let element spill outside container in this case.
        container.style.overflow = 'visible';
        disposeScaleControl(container);
    }
};

function scaleUpElement(
    container: HTMLElement,
    element: HTMLElement,
    needLogDatapoint: boolean
): number {
    let previousElementWidth = container.offsetWidth;
    let currentElementWidth: number;
    let minScale = MAX_SCALE;
    if (element.getAttribute(MIN_SCALE_ATTR_NAME)) {
        minScale = parseFloat(element.getAttribute(MIN_SCALE_ATTR_NAME));
        element.style.transform = 'scale(' + MAX_SCALE + ', ' + MAX_SCALE + ')';
        container.style.height = '';
        currentElementWidth = element.offsetWidth;
    } else {
        currentElementWidth = element.scrollWidth;
    }

    if (needLogDatapoint) {
        logUsage(datapoints.WCScaleUp.name, datapoints.WCScaleUp.customData(minScale));
    }

    currentElementWidth += getLeftRightMargins(element);
    return Math.max(currentElementWidth - previousElementWidth, 0);
}

function enlargeWideContentHost(
    wideContentHost: HTMLElement,
    element: HTMLElement,
    newWidthDelta: number
) {
    let hostWidth = wideContentHost.offsetWidth + newWidthDelta;
    // Add 20px padding right because scrollbar has overlay style in Chrome,
    // content will be cut off without padding.
    wideContentHost.style.width = hostWidth + 'px';
    wideContentHost.style.paddingRight = '20px';
    adjustScrollPosition(element, wideContentHost);
}

function shrinkWideContentHost(wideContentHost: HTMLElement) {
    wideContentHost.style.width = '';
    wideContentHost.style.paddingRight = '0';
}

function adjustScrollPosition(element: HTMLElement, wideContentHost: HTMLElement) {
    element.scrollIntoView();
    let scrollContainer = wideContentHost;

    // When calling element.scrollIntoView with horizontal scrollbar,
    // browsers (except for Chrome) tend to scroll to the right.
    // We want to keep the horizontal scrollbar to the very left.
    while (scrollContainer) {
        if (
            scrollContainer.scrollWidth > scrollContainer.offsetWidth &&
            window.getComputedStyle(scrollContainer, null).overflowX == 'auto'
        ) {
            // Found the scroll container with horizontal scrollbar present
            scrollContainer.scrollLeft = 0;
            break;
        } else {
            scrollContainer = scrollContainer.parentElement;
        }
    }
}

function wrap(element: HTMLElement): HTMLElement {
    let wrapper = document.createElement('div');
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);
    return wrapper;
}

function unwrap(element: HTMLElement) {
    let oldParent = element.parentNode;
    let newParent = oldParent.parentNode;
    newParent.insertBefore(element, oldParent);
    newParent.removeChild(oldParent);
}

function getLeftRightMargins(element: HTMLElement): number {
    let computedStyles = window.getComputedStyle(element, null);
    let marginLeft = parseInt(computedStyles.marginLeft) || 0;
    let marginRight = parseInt(computedStyles.marginRight) || 0;
    return marginLeft + marginRight;
}

function getTopBottomMargins(element: HTMLElement): number {
    let computedStyles = window.getComputedStyle(element, null);
    let marginTop = parseInt(computedStyles.marginTop) || 0;
    let marginBottom = parseInt(computedStyles.marginBottom) || 0;
    return marginTop + marginBottom;
}

function getWideContentHost(element: HTMLElement): HTMLElement {
    let wideContentHost: HTMLElement = null;
    while (element) {
        if (element.className == WIDE_CONTENT_HOST_CLASS_NAME) {
            wideContentHost = element;
            break;
        } else {
            element = element.parentElement;
        }
    }

    return wideContentHost;
}
